import { Component, ElementRef, OnInit, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";

@Component({
  selector: 'search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit, OnDestroy {

  searchForm!: FormGroup;
  searchString: string = '';
  itemsFound: { url: string, title?: string, occurrences: number }[] = [];

  private observer: MutationObserver | null = null;
  private linksProcessed: Set<string> = new Set();

  config: any = {
    treeMenuSelector: "tree-menu",
    linkAttribute: "data-url",
    appMainFrameSelector: "app-main-frame",
    titleSelector: "#title-obbligo",
    iframeId: "iframe-scan",
    maxConcurrentLoads: 3,
    debounceTime: 200,
    iframeDisplayStyle: "none",
  };

  constructor(private hostElement: ElementRef) { }

  ngOnInit(): void {
    this.searchForm = new FormGroup({
      searchText: new FormControl("", [Validators.minLength(4), Validators.required]),
    });
  }

  ngOnDestroy(): void {
    this.disconnectObserver();
  }

  /** Avvia l'osservazione del tree menu per individuare nuovi link */
  observeTreeMenu(): void {
    const treeMenu = document.querySelector(this.config.treeMenuSelector);
    if (!treeMenu) return;

    this.observer = new MutationObserver(this.debounce(() => this.processNewLinks(treeMenu), this.config.debounceTime));

    this.reactivateObserver(treeMenu);

  }

  /** Gestisce i nuovi link trovati */
  processNewLinks(treeMenu: Element): void {
    const newLinks = this.findNewLinks(treeMenu);

    if (newLinks.length === 0) {
      console.log('%cNessun nuovo link trovato.', 'color: orange;');
      this.reactivateObserver(treeMenu);
      return;
    }

    console.log('%cTrovati nuovi link:', 'color: orange;', newLinks);
    this.disconnectObserver();

    this.loadPagesWithLimit(newLinks)
      .then(() => this.logFinalOccurrences())
      .catch(error => console.error("Errore nel caricamento delle pagine:", error))
      .finally(() => this.reactivateObserver(treeMenu));
  }

  /** Trova nuovi link non ancora processati */
  findNewLinks(treeMenu: Element): string[] {
    return Array.from(treeMenu.querySelectorAll(`[${this.config.linkAttribute}]`))
      .map(el => el.getAttribute(this.config.linkAttribute) as string)
      .filter(link => !this.linksProcessed.has(link));
  }

  /** Carica le pagine in un iframe con un limite di richieste concorrenti */
  async loadPagesWithLimit(links: string[]): Promise<void> {
    const queue = [...links];
    const activePromises: Promise<void>[] = [];

    while (queue.length > 0 || activePromises.length > 0) {
      while (activePromises.length < this.config.maxConcurrentLoads && queue.length > 0) {
        const link = queue.shift()!;
        this.linksProcessed.add(link);
        console.log(`%cInizio caricamento: ${link}`, 'color: purple;');

        const promise = this.loadPageInIframe(`${window.location.origin}${link}`)
          .then(() => console.log(`%cCompletato: ${link}`, 'color: green;'))
          .catch(error => console.error(`Errore nel caricamento: ${link}`, error))
          .finally(() => this.removePromise(activePromises, promise));

        activePromises.push(promise);
      }
      await Promise.race(activePromises);
    }
  }

  /** Rimuove una promessa dall'elenco delle attive */
  removePromise(promises: Promise<void>[], promise: Promise<void>): void {
    const index = promises.indexOf(promise);
    if (index >= 0) promises.splice(index, 1);
  }

  /** Carica una pagina in un iframe e analizza il contenuto */
  loadPageInIframe(link: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const iframe = this.getOrCreateIframe();
      iframe.setAttribute('src', link);
      iframe.onload = () => this.checkIframeContent(iframe, link, resolve, reject);
      iframe.onerror = () => reject(new Error("Errore nel caricamento dell'iframe: " + link));
    });
  }

  /** Verifica il contenuto dell'iframe e calcola le occorrenze */
  checkIframeContent(iframe: HTMLIFrameElement, link: string, resolve: () => void, reject: (reason?: any) => void): void {
    const tryReadContent = () => {
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDocument) {
        reject(new Error("Impossibile accedere al documento dell'iframe."));
        return;
      }

      const appMainFrame = iframeDocument.querySelector(this.config.appMainFrameSelector);
      const title = iframeDocument.querySelector(this.config.titleSelector)?.textContent;

      if (appMainFrame && title) {
        const occurrences = this.countOccurrences(appMainFrame.outerHTML, this.searchString);
        this.itemsFound.push({ url: link, title, occurrences });
        console.log(`%cTrovato: ${occurrences} occorrenze in ${link}`, 'color: orange;');
        resolve();
      } else {
        setTimeout(tryReadContent, 100);
      }
    };

    tryReadContent();
  }

  /** Ottiene o crea un iframe nascosto */
  getOrCreateIframe(): HTMLIFrameElement {
    let iframe = this.hostElement.nativeElement.querySelector(`#${this.config.iframeId}`);
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = this.config.iframeId;
      iframe.style.display = this.config.iframeDisplayStyle;
      this.hostElement.nativeElement.appendChild(iframe);
    }
    return iframe;
  }

  /** Conta le occorrenze di una stringa */
  countOccurrences(content: string | undefined, searchString: string): number {
    if (!content) return 0;
    const matches = content.match(new RegExp(searchString, "gi"));
    return matches ? matches.length : 0;
  }

  /** Log finale delle occorrenze trovate */
  logFinalOccurrences(): void {
    console.log("%c--- Riepilogo finale ---", "color: blue;");
    this.itemsFound.forEach((item, index) => {
      console.log(
        `%c#${index + 1} - URL: ${item.url}, Titolo: ${item.title}, Occorrenze: ${item.occurrences}`,
        "color: green;"
      );
    });
  }

  /** Esegue il debounce di una funzione */
  debounce(func: Function, wait: number): () => void {
    let timeout: number | null = null;
    return (...args: any[]) => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
      timeout = window.setTimeout(() => func(...args), wait);
    };
  }

  /** Disconnette l'observer */
  disconnectObserver(): void {
    this.observer?.disconnect();
  }

  /** Riattiva l'observer */
  reactivateObserver(treeMenu: Element): void {
    this.observer?.observe(treeMenu, { childList: true, subtree: true });
  }

  /**
   * On sumbit search form
   */
  onSubmitSearch(): void {
    if (!this.searchForm.valid) return;
    console.log(this.searchString);
  }

}
